// 空间管理应用主文件
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, useRoutes } from "react-router-dom";
import { ConfigProvider, App as AntApp, Spin } from "antd";
import zhCN from "antd/lib/locale/zh_CN";

// 导入集中式配置
import { themeConfig, routerConfig, msSpaceAuthConfig } from "./config";
import { getRouteObjects } from "./routes";
import { initializeApp } from "./service/appInitializer";

// 导入统一认证系统
import { useAuthWithConfig } from "@ms/shared-auth";

// 样式
import "./App.css";

/**
 * 路由组件 - 带认证保护
 */
const AppRoutes: React.FC = () => {
  // 使用 useMemo 缓存路由配置，避免每次渲染都创建新的路由对象
  const routeObjects = React.useMemo(() => {
    return getRouteObjects();
  }, []);

  const routes = useRoutes(routeObjects);
  return routes;
};

/**
 * 带认证的应用组件
 */
const AppWithAuth: React.FC = () => {
  console.log("[msSpace] AppWithAuth 组件开始渲染");
  console.log("[msSpace] 当前环境:", {
    origin: window.location.origin,
    href: window.location.href,
    isIframe: window.parent !== window,
    userAgent: navigator.userAgent.substring(0, 50),
  });

  // 使用配置化的认证系统
  const { isAuthenticated, loading, error, user, checkAuth, login } =
    useAuthWithConfig(msSpaceAuthConfig);

  console.log("[msSpace] useAuth 返回状态:", {
    isAuthenticated,
    loading,
    error: !!error,
    hasUser: !!user,
  });

  React.useEffect(() => {
    console.log("[msSpace] 开始执行认证检查 useEffect");

    // 检查URL参数中是否有token
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get("token");
    console.log("[msSpace] URL参数检查:", {
      urlToken: urlToken ? "存在" : "不存在",
    });

    if (urlToken) {
      console.log("[msSpace] 从URL获取到token，开始登录");
      login(urlToken);
      // 清除URL中的token参数
      const url = new URL(window.location.href);
      url.searchParams.delete("token");
      window.history.replaceState({}, "", url.toString());
    } else {
      // 检查本地是否有token和用户缓存
      const localToken =
        localStorage.getItem(msSpaceAuthConfig.tokenKey) ||
        sessionStorage.getItem(msSpaceAuthConfig.tokenKey);
      const cachedUser = localStorage.getItem("mslogin_user");

      console.log("[msSpace] Token检查结果:", {
        localToken: localToken ? "存在" : "不存在",
        cachedUser: cachedUser ? "存在" : "不存在",
        tokenLength: localToken ? localToken.length : 0,
        isIframe: window.parent !== window,
        currentOrigin: window.location.origin,
        tokenKey: msSpaceAuthConfig.tokenKey,
      });

      // 如果有token和缓存，且已经认证，不需要再次调用checkAuth
      // useAuthWithConfig内部已经处理了缓存和验证
      if (localToken && cachedUser && isAuthenticated && !loading) {
        console.log("[msSpace] 已有缓存和认证状态，跳过重复验证");
        return;
      }

      if (localToken) {
        // 有本地token但没有缓存或未认证，进行认证检查
        // 但只在loading为false时调用，避免重复验证
        if (!loading) {
          console.log("[msSpace] 使用本地token进行认证");
          console.log("[msSpace] Token详情:", {
            token: localToken.substring(0, 50) + "...",
            length: localToken.length,
            tokenKey: msSpaceAuthConfig.tokenKey,
          });
          checkAuth();
        }
      } else {
        // 没有token，检查是否是独立访问模式
        const isStandalone =
          window.location.search.includes("standalone=true") ||
          localStorage.getItem("msSpace_standalone") === "true";

        if (isStandalone) {
          console.log("[msSpace] 独立模式，允许无认证访问");
          // 在独立模式下，允许访问但显示登录提示
        } else if (window.parent !== window) {
          // 在iframe环境中，等待父页面提供token
          console.log("[msSpace] 在iframe环境中，等待父页面提供token");
          // 请求父页面提供token
          window.parent.postMessage(
            { type: "TOKEN_REQUEST", service: "msspace" },
            "*"
          );
          // 不跳转，等待token
        } else {
          // 没有token，不跳转页面，而是等待token
          console.log("[msSpace] 没有本地token，等待token");
          // const currentUrl = window.location.href;
          // const loginUrl = `${msSpaceAuthConfig.msLoginUrl}${
          //   msSpaceAuthConfig.loginUrl
          // }?redirect=${encodeURIComponent(currentUrl)}`;
          // window.location.href = loginUrl;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 空依赖数组，只在组件挂载时执行一次

  // 监听来自msLogin和父页面的Token更新消息
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log("[msSpace] 收到消息:", {
        origin: event.origin,
        type: event.data.type,
        service: event.data.service,
      });

      // 处理来自父页面的TOKEN_PROVIDE消息
      if (
        event.data.type === "TOKEN_PROVIDE" &&
        event.data.service === "msspace"
      ) {
        console.log("[msSpace] 收到父页面提供的token");
        if (event.data.token) {
          // 保存token到本地存储
          localStorage.setItem(msSpaceAuthConfig.tokenKey, event.data.token);
          localStorage.setItem("mslogin_token", event.data.token);
          console.log("[msSpace] Token已保存，开始认证");
          checkAuth();
        }
        return;
      }

      // 处理来自父页面的通用token传递
      if (event.data.type === "TOKEN_PROVIDE" && event.data.token) {
        console.log("[msSpace] 收到父页面提供的通用token");
        // 保存token到本地存储
        localStorage.setItem(msSpaceAuthConfig.tokenKey, event.data.token);
        localStorage.setItem("mslogin_token", event.data.token);
        console.log("[msSpace] 通用Token已保存，开始认证");
        checkAuth();
        return;
      }

      // 处理来自msLogin的消息
      if (
        event.origin !==
        msSpaceAuthConfig.msLoginUrl
          .replace("http://", "http://")
          .replace("https://", "https://")
      ) {
        return;
      }

      switch (event.data.type) {
        case "TOKEN_UPDATED":
          console.log("[msSpace] 收到Token更新通知");
          if (event.data.token) {
            // 更新本地存储的Token
            localStorage.setItem(msSpaceAuthConfig.tokenKey, event.data.token);
            localStorage.setItem("mslogin_token", event.data.token);
            console.log("[msSpace] Token已更新");
          }
          break;
        case "AUTH_FAILED":
          console.log("[msSpace] 收到认证失败通知");
          // 清除本地Token
          localStorage.removeItem(msSpaceAuthConfig.tokenKey);
          localStorage.removeItem("mslogin_token");
          localStorage.removeItem("mslogin_user");
          // 通知父应用处理认证失败
          if (window.parent !== window) {
            window.parent.postMessage({ type: "AUTH_FAILED" }, "*");
          }
          break;
        case "AUTH_CLEAR":
          console.log("[msSpace] 收到清除认证通知");
          // 清除所有本地Token
          localStorage.removeItem(msSpaceAuthConfig.tokenKey);
          localStorage.removeItem("mslogin_token");
          localStorage.removeItem("mslogin_user");
          sessionStorage.removeItem(msSpaceAuthConfig.tokenKey);
          // 不刷新页面，而是重新进行认证检查
          // window.location.reload();
          break;
        default:
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []); // 空依赖数组，只在组件挂载时执行一次

  // 请求Token更新（当微服务启动时）
  React.useEffect(() => {
    // 在iframe环境中，请求最新的Token
    if (window.parent !== window) {
      console.log("[msSpace] 在iframe环境中，请求Token更新");
      window.parent.postMessage({ type: "TOKEN_UPDATE_REQUEST" }, "*");
    }
  }, []);

  // 认证失败时跳转到 msLogin
  React.useEffect(() => {
    if (error) {
      const isStandalone =
        window.location.search.includes("standalone=true") ||
        localStorage.getItem("msSpace_standalone") === "true";

      if (!isStandalone) {
        // 不跳转页面，而是等待重新认证
        console.log("[msSpace] 认证失败，等待重新认证");
        // const currentUrl = window.location.href;
        // const loginUrl = `${msSpaceAuthConfig.msLoginUrl}${
        //   msSpaceAuthConfig.loginUrl
        // }?redirect=${encodeURIComponent(currentUrl)}`;
        // window.location.href = loginUrl;
      } else {
        console.log("[msSpace] 独立模式，认证失败但不跳转");
      }
    }
  }, [error]);

  // 参考 msAdmin 的实现，不检查 loading 状态，直接渲染路由
  // AuthGuard 组件会处理认证和权限检查
  // 这样可以避免页面切换时的闪烁
  return <AppRoutes />;
};

/**
 * 主应用组件
 */
const App: React.FC = () => {
  console.log("🚀 App组件初始化, 当前路径:", window.location.pathname);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 环境初始化
  useEffect(() => {
    const init = async () => {
      try {
        console.log("🔧 初始化应用...");

        // 初始化应用
        const result = await initializeApp();
        if (!result.success) {
          setError(result.error ?? null);
        }
      } catch (error: any) {
        console.error("❌ 应用初始化失败:", error);
        setError(error.message || "应用初始化失败");
      }
      setLoading(false);
    };

    init();
  }, []);

  // 应用加载中状态
  if (loading) {
    return (
      <div className="app-loading">
        <Spin size="large" tip="初始化应用环境..." fullscreen />
      </div>
    );
  }

  // 应用加载失败状态
  if (error) {
    return (
      <div
        className="app-error"
        style={{ textAlign: "center", padding: "50px 20px" }}
      >
        <h2>应用加载失败</h2>
        <p>{error}</p>
        <button
          onClick={() => {
            // 不刷新页面，而是重新进行认证检查
            // window.location.reload();
          }}
          style={{ padding: "8px 16px", cursor: "pointer" }}
        >
          重新加载
        </button>
      </div>
    );
  }

  return (
    <ConfigProvider locale={zhCN} theme={themeConfig}>
      <AntApp>
        <Router future={routerConfig.futureConfig}>
          <AppWithAuth />
        </Router>
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
